import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { ApiError, apiRequest } from '../lib/api-client';

type StockStatus = 'SAFE' | 'LOW_STOCK' | 'OUT_OF_STOCK';
type InventoryItem = {
  id: string;
  name: string;
  sku: string;
  quantity: number;
  minimum: number;
  status: StockStatus;
  averageUnitCost: string;
  inventoryValue: string;
};
type Movement = {
  id: string;
  type: string;
  quantity: number;
  balanceBefore?: number;
  balanceAfter: number;
  averageCostBefore?: string;
  averageCostAfter?: string;
  notes?: string;
  createdAt: string;
  user?: { name: string };
};

const statusStyle: Record<StockStatus, string> = {
  SAFE: 'bg-emerald-50 text-emerald-700',
  LOW_STOCK: 'bg-amber-50 text-amber-700',
  OUT_OF_STOCK: 'bg-red-50 text-red-700',
};
const statusLabel: Record<StockStatus, string> = {
  SAFE: 'Aman',
  LOW_STOCK: 'Menipis',
  OUT_OF_STOCK: 'Habis',
};
const reasonOptions = [
  ['STOCK_OPNAME_IN', 'Stock opname +', 'IN'],
  ['STOCK_OPNAME_OUT', 'Stock opname −', 'OUT'],
  ['DAMAGED', 'Barang rusak', 'OUT'],
  ['LOST', 'Barang hilang', 'OUT'],
  ['ADMINISTRATIVE_CORRECTION', 'Koreksi administrasi', 'BOTH'],
  ['INTERNAL_USE', 'Penggunaan internal', 'OUT'],
] as const;

export function Inventory({
  canAdjust,
  canReceive,
  outletReady,
  onOpenReceipts,
}: {
  canAdjust: boolean;
  canReceive: boolean;
  outletReady: boolean;
  onOpenReceipts: () => void;
}) {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [dialog, setDialog] = useState<InventoryItem | null>(null);
  const [direction, setDirection] = useState<'IN' | 'OUT'>('OUT');
  const [quantity, setQuantity] = useState('1');
  const [reason, setReason] = useState('DAMAGED');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [historyItem, setHistoryItem] = useState<InventoryItem | null>(null);
  const [history, setHistory] = useState<Movement[]>([]);

  async function load() {
    if (!outletReady) return;
    setLoading(true);
    try {
      const result = await apiRequest<{ data: InventoryItem[]; meta: { total: number } }>(
        `/inventory?limit=10&page=${page}&search=${encodeURIComponent(query)}`,
      );
      setItems(result.data);
      setTotal(result.meta.total);
      setError('');
    } catch (value) {
      setError(value instanceof ApiError ? value.message : 'Daftar stok gagal dimuat.');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, [outletReady, page, query]);

  function openAdjustment(item: InventoryItem) {
    setDialog(item);
    setDirection('OUT');
    setReason('DAMAGED');
    setQuantity('1');
    setNotes('');
    setError('');
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!dialog) return;
    const amount = Number(quantity);
    if (
      !Number.isInteger(amount) ||
      amount < 1 ||
      (direction === 'OUT' && amount > dialog.quantity)
    ) {
      setError('Jumlah tidak valid atau stok tidak mencukupi.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await apiRequest('/inventory/adjustments', {
        method: 'POST',
        body: JSON.stringify({
          productId: dialog.id,
          direction,
          quantity: amount,
          reason,
          notes: notes.trim(),
        }),
      });
      setMessage(`Penyesuaian stok ${dialog.name} berhasil dicatat.`);
      setDialog(null);
      await load();
    } catch (value) {
      setError(value instanceof ApiError ? value.message : 'Penyesuaian gagal disimpan.');
    } finally {
      setBusy(false);
    }
  }

  async function openHistory(item: InventoryItem) {
    setHistoryItem(item);
    setHistory([]);
    try {
      setHistory(
        (
          await apiRequest<{ data: Movement[] }>(
            `/inventory/history?productId=${item.id}&limit=100`,
          )
        ).data,
      );
    } catch (value) {
      setError(value instanceof ApiError ? value.message : 'Riwayat gagal dimuat.');
      setHistoryItem(null);
    }
  }

  if (!outletReady)
    return (
      <div className="rounded-2xl bg-amber-50 p-6 text-amber-800">
        Pilih outlet untuk membuka inventory.
      </div>
    );
  return (
    <section className="page-shell">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="font-bold text-ajisai-700">Barang Jadi</p>
          <h1 className="mt-1 text-3xl font-black">Inventory Barang Jadi</h1>
          <p className="mt-2 text-sm text-slate-500">
            Inventory adalah sumber kebenaran stok outlet. Restock normal dicatat melalui Penerimaan
            Barang.
          </p>
        </div>
        {canReceive && (
          <button
            className="rounded-xl bg-ajisai-700 px-5 py-3 text-sm font-bold text-white"
            onClick={onOpenReceipts}
          >
            + Penerimaan Barang
          </button>
        )}
      </div>
      {error && (
        <p role="alert" className="mt-5 rounded-xl bg-red-50 p-4 text-sm text-red-700">
          {error}
        </p>
      )}
      {message && (
        <p className="mt-5 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-700">{message}</p>
      )}
      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <form
          className="flex gap-2 border-b p-4"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setQuery(search.trim());
          }}
        >
          <input
            className="min-w-0 flex-1 rounded-xl border px-4 py-2.5"
            placeholder="Cari produk atau SKU"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button className="rounded-xl bg-slate-900 px-5 text-white">Cari</button>
        </form>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-5 py-3">Produk</th>
                <th>Stok</th>
                <th>Minimum</th>
                <th>Status</th>
                <th className="px-5 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="px-5 py-4">
                    <b>{item.name}</b>
                    <p className="text-xs text-slate-500">{item.sku}</p>
                  </td>
                  <td className="font-black">{item.quantity}</td>
                  <td>{item.minimum}</td>
                  <td>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-bold ${statusStyle[item.status]}`}
                    >
                      {statusLabel[item.status]}
                    </span>
                  </td>
                  <td className="px-5 text-right">
                    {canAdjust && (
                      <button
                        className="mr-2 rounded-lg border px-3 py-2 text-xs font-bold"
                        onClick={() => openAdjustment(item)}
                      >
                        Penyesuaian
                      </button>
                    )}
                    <button
                      className="rounded-lg border px-3 py-2 text-xs font-bold"
                      onClick={() => void openHistory(item)}
                    >
                      Riwayat
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {loading && <p className="p-10 text-center text-sm text-slate-500">Memuat...</p>}
        {!loading && !items.length && (
          <p className="p-10 text-center text-sm text-slate-500">Produk tidak ditemukan.</p>
        )}
        <div className="flex justify-between border-t p-4 text-sm">
          <span>{total} produk</span>
          <div className="flex gap-2">
            <button
              disabled={page === 1}
              className="rounded border px-3 py-1 disabled:opacity-40"
              onClick={() => setPage((v) => v - 1)}
            >
              Sebelumnya
            </button>
            <button
              disabled={page * 10 >= total}
              className="rounded border px-3 py-1 disabled:opacity-40"
              onClick={() => setPage((v) => v + 1)}
            >
              Berikutnya
            </button>
          </div>
        </div>
      </div>
      {dialog && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/40 p-4">
          <form
            className="w-full max-w-md rounded-2xl bg-white p-6"
            onSubmit={(e) => void submit(e)}
          >
            <h2 className="text-xl font-black">Penyesuaian Stok</h2>
            <p className="mt-1 text-sm text-slate-500">
              {dialog.name} · stok {dialog.quantity}
            </p>
            <label className="mt-5 block text-sm font-bold">
              Arah
              <select
                className="mt-2 w-full rounded-xl border p-3"
                value={direction}
                onChange={(e) => {
                  const value = e.target.value as 'IN' | 'OUT';
                  setDirection(value);
                  setReason(value === 'IN' ? 'STOCK_OPNAME_IN' : 'DAMAGED');
                }}
              >
                <option value="IN">Tambah karena koreksi/opname</option>
                <option value="OUT">Kurangi karena koreksi/kerusakan</option>
              </select>
            </label>
            <label className="mt-4 block text-sm font-bold">
              Alasan
              <select
                className="mt-2 w-full rounded-xl border p-3"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              >
                {reasonOptions
                  .filter(([, , allowed]) => allowed === direction || allowed === 'BOTH')
                  .map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
              </select>
            </label>
            <label className="mt-4 block text-sm font-bold">
              Jumlah
              <input
                className="mt-2 w-full rounded-xl border p-3"
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </label>
            <label className="mt-4 block text-sm font-bold">
              Catatan wajib
              <textarea
                required
                minLength={3}
                className="mt-2 w-full rounded-xl border p-3"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </label>
            <p className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
              Pembelian/restock normal tidak boleh dicatat sebagai penyesuaian.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                type="button"
                className="rounded-xl border p-3 font-bold"
                onClick={() => setDialog(null)}
              >
                Batal
              </button>
              <button disabled={busy} className="rounded-xl bg-ajisai-700 p-3 font-bold text-white">
                {busy ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </form>
        </div>
      )}
      {historyItem && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/40 p-4">
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6">
            <div className="flex justify-between">
              <div>
                <p className="text-sm font-bold text-ajisai-700">Riwayat Stok</p>
                <h2 className="text-xl font-black">{historyItem.name}</h2>
              </div>
              <button onClick={() => setHistoryItem(null)}>×</button>
            </div>
            <div className="mt-5 space-y-3">
              {history.map((movement) => (
                <article key={movement.id} className="rounded-xl border p-4">
                  <div className="flex justify-between">
                    <b>{movement.type.replaceAll('_', ' ')}</b>
                    <b className={movement.quantity >= 0 ? 'text-emerald-700' : 'text-red-700'}>
                      {movement.quantity > 0 ? '+' : ''}
                      {movement.quantity}
                    </b>
                  </div>
                  <p className="mt-1 text-sm">
                    Stok {movement.balanceBefore ?? '?'} → {movement.balanceAfter}
                  </p>
                  {movement.notes && (
                    <p className="mt-1 text-sm text-slate-500">{movement.notes}</p>
                  )}
                  <p className="mt-2 text-xs text-slate-400">
                    {new Date(movement.createdAt).toLocaleString('id-ID')} ·{' '}
                    {movement.user?.name ?? 'Sistem'}
                  </p>
                </article>
              ))}
              {!history.length && (
                <p className="rounded-xl bg-slate-50 p-8 text-center text-sm">Belum ada riwayat.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
